import { TargetGroupSidebar } from "@/components/campaign/TargetGroupSidebar";
import { AlertModal } from "@/components/generic/AlertModal";
import { trialText } from "@/hooks/use-trial-period";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { TargetGroupProvider } from "@/providers/TargetGroupProvider";
import {
  getConfigurationSuggestions,
  useConfigurationCounts,
} from "@/queries/configurations";
import { Configuration } from "@/types/campaign";
import { assertDefined } from "@/utils/common";
import { store } from "@/utils/jotai";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useBlocker } from "@tanstack/react-router";
import isEqual from "lodash-es/isEqual";
import { useEffect, useMemo, useState } from "react";

const editConfigSuggestionsSymbol = Symbol();

export const Route = createFileRoute("/_dashboard/campaigns/$id/(edit)/define")(
  {
    component: RouteComponent,
    beforeLoad: async ({ context, params }) => {
      const createAppliedConfigurationSuggestionsQueryOptions = (
        mainConfiguration: Configuration,
      ) => {
        const configurationSuggestionsQueryOptions =
          getConfigurationSuggestions({
            configuration: mainConfiguration,
            campaignId: params.id,
          });

        return {
          configurationSuggestionsQueryOptions,
          appliedConfigurationSuggestionsQueryOptions: queryOptions({
            queryKey: [editConfigSuggestionsSymbol, mainConfiguration],
            queryFn: async ({ client, signal }) => {
              const suggestions = await client.ensureQueryData(
                getConfigurationSuggestions({
                  configuration: mainConfiguration,
                  campaignId: params.id,
                }),
              );

              signal.throwIfAborted();

              // TODO - unsure if context.editCampaignAtom is the way to go here - it works but feels a bit hacky
              const editCampaign = store.get(context.editCampaignAtom);

              if (!editCampaign) return [];

              const existingConfigurationsByQuality = new Map(
                editCampaign.configurations.map((configuration) => [
                  configuration.quality,
                  configuration,
                ]),
              );

              const applySuggestion = (
                existingConfiguration: Configuration | undefined,
                suggestedConfiguration: Configuration,
              ) => ({
                ...suggestedConfiguration,
                priority:
                  existingConfiguration?.priority ??
                  suggestedConfiguration.priority,
                subPriority:
                  existingConfiguration?.subPriority ??
                  suggestedConfiguration.subPriority,
                idealLeadShare:
                  existingConfiguration?.idealLeadShare ??
                  suggestedConfiguration.idealLeadShare,
                id: existingConfiguration?.id, // Make sure to keep the existing ID to avoid creating new configurations when applying suggestions
              });

              let hasChanges = false;

              const updatedConfigurations = suggestions.map((s) => {
                const existingConfiguration =
                  existingConfigurationsByQuality.get(s.configuration.quality);

                // Gold config: keep existing reference untouched
                if (existingConfiguration?.quality === 1) {
                  return existingConfiguration;
                }

                // Enabled non-gold: keep existing reference untouched
                if (existingConfiguration?.enabled) {
                  return existingConfiguration;
                }

                // Disabled non-gold: apply suggestion
                const suggested = {
                  ...applySuggestion(existingConfiguration, s.configuration),
                  enabled: false,
                };

                // Avoid false dirty state: only swap if structurally different
                if (
                  existingConfiguration &&
                  isEqual(existingConfiguration, suggested)
                ) {
                  return existingConfiguration;
                }

                hasChanges = true;
                return suggested;
              });

              // Sanity check: ensure the main configuration hasn't changed since we started
              if (
                hasChanges &&
                editCampaign.configurations.find(
                  (c) => c.quality === mainConfiguration.quality,
                ) === mainConfiguration
              ) {
                const updatedMainConfiguration = updatedConfigurations.find(
                  (c) => c.quality === 1,
                );

                if (updatedMainConfiguration) {
                  client.setQueryData(
                    getConfigurationSuggestions({
                      configuration: updatedMainConfiguration,
                    }).queryKey,
                    suggestions,
                  );
                }

                store.set(context.editCampaignAtom, {
                  ...editCampaign,
                  configurations: updatedConfigurations,
                });
              }

              return updatedConfigurations;
            },
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            staleTime: Infinity,
          }),
        };
      };

      const mainConfiguration = assertDefined(
        store
          .get(context.editCampaignAtom)
          ?.configurations.find((c) => c.quality === 1),
      );

      const {
        configurationSuggestionsQueryOptions,
        appliedConfigurationSuggestionsQueryOptions,
      } = createAppliedConfigurationSuggestionsQueryOptions(mainConfiguration);

      const useAppliedConfigurationSuggestions = () => {
        const { editCampaign } = context.useEditCampaign();

        const mainConfiguration = useMemo(
          () =>
            assertDefined(
              editCampaign.configurations.find((c) => c.quality === 1),
            ),
          [editCampaign.configurations],
        );

        const {
          configurationSuggestionsQueryOptions,
          appliedConfigurationSuggestionsQueryOptions,
        } = useMemo(
          () =>
            createAppliedConfigurationSuggestionsQueryOptions(
              mainConfiguration,
            ),
          [mainConfiguration],
        );

        const configurationSuggestionsQuery = useQuery(
          configurationSuggestionsQueryOptions,
        );

        const appliedConfigurationSuggestionsQuery = useQuery(
          appliedConfigurationSuggestionsQueryOptions,
        );

        return {
          configurationSuggestionsQuery,
          appliedConfigurationSuggestionsQuery,
        };
      };

      return {
        configurationSuggestionsQueryOptions,
        appliedConfigurationSuggestionsQueryOptions,
        useAppliedConfigurationSuggestions,
      };
    },
    loader: async () => {
      return {
        isBreadcrumbRoot: true,
        crumb: "Målgruppe",
        meta: {
          title: "Målgruppe",
        },
      };
    },
  },
);

function RouteComponent() {
  const { useEditCampaign, useTrialCTAModal, trialPeriod } =
    Route.useRouteContext();
  const navigate = Route.useNavigate();

  const { editCampaign, isDirty, resetCampaign } = useEditCampaign();

  const [_, setTrialCTAModalProps] = useTrialCTAModal();

  const { isLoading: isCountsLoading } = useConfigurationCounts({
    configurations: editCampaign.configurations,
    stopListIds:
      editCampaign.stopLists?.map((s) => s.uniqueId.toString()) ?? [],
    campaignId: editCampaign.id,
  });

  const [, setLoadingCount] = useState(
    () => Math.floor(Math.random() * 9000) + 1000,
  );

  const blocker = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (!isDirty) return false;
      if (next.routeId.startsWith(Route.id)) return false;
      return true;
    },
    withResolver: true,
  });

  useEffect(() => {
    if (!isCountsLoading) return;

    const interval = setInterval(() => {
      setLoadingCount(Math.floor(Math.random() * 9000) + 1000);
    }, 100);

    return () => clearInterval(interval);
  }, [isCountsLoading]);

  return (
    <>
      <PageHeaderProvider
        options={{
          title: "Målgruppe",
        }}
      >
        <TargetGroupProvider>
          <div className="flex h-full flex-col justify-between">
            <Outlet />
          </div>
          <TargetGroupSidebar
            configurations={editCampaign.configurations}
            stopLists={editCampaign.stopLists}
            onStopListClick={() => {
              if (trialPeriod) {
                setTrialCTAModalProps({
                  title: trialText,
                });
                return;
              }
              navigate({
                to: "/campaigns/$id/define/stop-lists",
              });
            }}
            onFilterGroupSelect={(filterGroupId, category) => {
              navigate({
                to: "/campaigns/$id/define/$category/$filter",
                params: {
                  filter: filterGroupId,
                  category,
                },
              });
            }}
          />
        </TargetGroupProvider>
      </PageHeaderProvider>
      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={() => {
          resetCampaign();
          blocker.proceed?.();
        }}
      />
    </>
  );
}
